import React from 'react';
import { useMediaQuery } from '@material-ui/core';
import { uniq, get } from 'lodash';
import StubPagePerformanceContent from '../../components/StubPagePerformanceContent';
import { TP, PRODUCTION_VIEW_TYPES, FESTIVAL_TAB_VALUES, PAGE_NAMES, dateFormats } from '../../constants/consts';
import Subfilter from '../Subfilter';
import { Router } from '../../src/i18n';
import { getProductions, getPerformances } from './queries';
import { createDate } from '../../utils/date';
import { getRoutePartWithSlug } from '../../utils/common';
import { getSubFilterOptions } from '../Subfilter/Component/config';
import route from '../../constants/routes';

function contentReducer(state, { type, payload }) {
  switch (type) {
    case 'UPDATE_QUERY':
      return {
        ...state,
        query: { ...state.query, ...payload.query },
      };
    case 'RESET_QUERY':
      return {
        ...state,
        query: { ...payload.query },
      };
    case 'UPDATE_VIEW':
      return {
        ...state,
        viewType: payload.viewType,
      };
    case 'UPDATE_TAB_VIEW':
      return {
        ...state,
        tabViewType: payload.tabViewType,
      };
    case 'LOADING_PRODUCTIONS':
      return {
        ...state,
        isLoadingProductions: true,
      };
    case 'LOADED_PRODUCTIONS':
      const { productions } = payload;
      if (state?.productions?.data?.length > 0 && productions?.page > 1) {
        productions.data.unshift(...state.productions.data);
        const data = [...state?.productions?.data, ...productions.data];
        productions.data = uniq(data);
      }
      return {
        ...state,
        isLoadingProductions: false,
        productions,
      };
    default: {
      throw new Error(`Unsupported type: ${type}`);
    }
  }
}

const getId = (page, entityId) => {
  const id = entityId.toString();
  switch (page) {
    case 'artist':
      return { profile_id: id };
    case 'production':
      return { id };
    case 'company':
      return { company_id: id };
    case 'festival':
      return { festival_id: id };
    case 'venues':
      return { venue_id: id };
    case 'works':
      return { work_id: id };
    default: {
      throw new Error(`Unsupported type: ${page}`);
    }
  }
};

const getTitle = (page, t) => {
  switch (page) {
    case 'artist':
    case 'production':
    case 'festival':
    case 'venues':
      return t(`${TP}.m_PERF`);
    case 'company':
      return t(`${TP}.m_SEASON`);
    default: {
      return t(`${TP}.m_PERF`);
    }
  }
};

const SubFilterSection = React.memo(
  ({
    options,
    query,
    onChange,
    isSmall,
    className,
    selectedValueQuery,
    seasonsOptions,
    tabViewType,
    hasSubFilterSelectedValues,
  }) => {
    const { language_id, subtitle_id, country_id, year_id, season_id } = query;
    const isNewSearch =
      hasSubFilterSelectedValues ||
      (typeof language_id === 'undefined' &&
        typeof subtitle_id === 'undefined' &&
        typeof country_id === 'undefined' &&
        typeof year_id === 'undefined' &&
        typeof season_id === 'undefined');
    return (
      <section className={className && className.subFilter_section}>
        <Subfilter
          options={options}
          query={query}
          onChange={val => {
            onChange(val);
          }}
          isSmall={isSmall}
          tabViewType={tabViewType}
          isNewSearch={isNewSearch}
          seasonsOptions={seasonsOptions}
          selectedValueQuery={selectedValueQuery}
        />
      </section>
    );
  },
);

export const StubPagePerformances = ({
  t,
  tab,
  page,
  isLoggedIn,
  entityId,
  profile,
  className,
  linkProps,
  dates,
  queryValues,
  pageSpecificValues,
  initialPerformances,
  hideViewPerformance,
  showSubFilters = true,
  seasonsOptions,
  festivalInfo,
}) => {
  const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const subFilterOptions = getSubFilterOptions(t, page);
  const queryId = getId(page, entityId);
  const initialQuery = {
    ...queryId,
    ...queryValues,
  };
  const hasSubFilterSelectedValues = typeof pageSpecificValues !== 'undefined';
  const [state, dispatch] = React.useReducer(contentReducer, {
    ...queryId,
    isLoadingProductions: false,
    productions: initialPerformances,
    tabViewType: 'all',
    viewType: page === PAGE_NAMES.PRODUCTION ? PRODUCTION_VIEW_TYPES.PERFORMANCE : PRODUCTION_VIEW_TYPES.PRODUCTION,
    query: {
      ...initialQuery,
      ...(pageSpecificValues && pageSpecificValues.query && { ...pageSpecificValues.query }),
    },
  });

  React.useEffect(() => {
    fetchPerformances(state.query, { limit: 10, page: 1 }, state.viewType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.query]);

  const resetQuery = query => {
    dispatch({
      type: 'RESET_QUERY',
      payload: {
        query,
      },
    });
  };

  const onSubfilterChange = subfilterValues => {
    const { filterType, selectedValue } = subfilterValues;
    let newAggregationQuery = {};
    if (selectedValue) {
      if (filterType === 'language_id') {
        newAggregationQuery = { ...state.query, ...{ language_id: selectedValue.id } };
      }
      if (filterType === 'subtitle_id') {
        newAggregationQuery = { ...state.query, ...{ subtitle_id: selectedValue.id } };
      }
      if (filterType === 'country_id') {
        newAggregationQuery = { ...state.query, ...{ country_id: selectedValue.id } };
      }

      if (filterType === 'season_id') {
        const { date_from, date_to, ...rest } = state.query; // ignore date_from/to if seasons are selected
        newAggregationQuery = { ...rest, ...{ season_id: selectedValue.id } };
        if (pageSpecificValues.route.updateRoute) {
          Router.push(
            {
              pathname: pageSpecificValues.route.config.href,
              query: { season_id: selectedValue.slug },
            },
            `${pageSpecificValues.route.config.as}?season_id=${selectedValue.slug}`,
            {
              shallow: true,
            },
          );
        }
      }

      if (filterType === 'year_id') {
        Router.push(
          festivalInfo.href,
          `${route.FESTIVALS}/${selectedValue.id}/${get(festivalInfo?.festival, 'country.slug')}/${getRoutePartWithSlug(
            festivalInfo?.festival,
          )}/${FESTIVAL_TAB_VALUES.PERFORMANCES}`,
        );
        newAggregationQuery = { ...state.query, ...{ year_id: selectedValue.id } };
      }

      if (filterType === 'date') {
        const localQuery = { ...state.query };
        if (selectedValue.season_id) {
          delete localQuery.date_from;
          delete localQuery.date_to;
        } else delete localQuery.season_id;
        newAggregationQuery = {
          ...localQuery,
          ...{ ...selectedValue },
        };
      }
    } else {
      const updatedQuery = { ...state.query };
      delete updatedQuery[filterType];
      newAggregationQuery = { ...updatedQuery };
    }
    resetQuery(newAggregationQuery);
  };

  const fetchPerformances = (aggregationQuery, pagination, thisViewType) => {
    const query = JSON.parse(JSON.stringify(aggregationQuery));
    if (page === 'production' && query.id) {
      const { id } = query;
      delete query.id;
      query.production_id = id;
    }
    if (page !== 'production') {
      if (query.date_from && query.date_to)
        query.sort = thisViewType === PRODUCTION_VIEW_TYPES.PRODUCTION ? 'year' : 'date';
      else query.sort = thisViewType === PRODUCTION_VIEW_TYPES.PRODUCTION ? '-year' : '-date';
    }
    if (festivalInfo?.year) query.year_id = get(festivalInfo, 'year');

    dispatch({
      type: 'LOADING_PRODUCTIONS',
    });
    if (thisViewType === PRODUCTION_VIEW_TYPES.PRODUCTION) {
      getProductions(query, pagination, page).then(response => {
        dispatch({
          type: 'LOADED_PRODUCTIONS',
          payload: {
            productions: response,
          },
        });
      });
    } else {
      getPerformances(query, pagination, page).then(response => {
        dispatch({
          type: 'LOADED_PRODUCTIONS',
          payload: {
            productions: response,
          },
        });
      });
    }
  };

  const goToTab = () => {
    Router.push(linkProps.href, linkProps.as, {
      shallow: true,
    });
  };

  const fetchPerformanceForMonth = firstDayOfMonth => {
    const dateFrom = new Date(firstDayOfMonth);
    const dateTo = new Date(dateFrom.getFullYear(), dateFrom.getMonth() + 1, 0);
    const calendarQuery = {
      ...state.query,
      date_from: createDate(dateFrom).format('YYYY-MM-DD'),
      date_to: createDate(dateTo).format('YYYY-MM-DD'),
      sort: 'date',
    };
    fetchPerformances(calendarQuery, { limit: 1000, page: 1 }, state.viewType);
  };

  const getSeason = () => {
    if (state.query.season_id) {
      return { season_id: state.query.season_id };
    }

    return null;
  };

  const performanceTabs = [
    {
      name: t(`${TP}.FN_ALL_PERFORMANCES`),
      value: 'all',
      isShown: true,
    },
    {
      name: t(`${TP}.m_ARTIST_UPCOMING_PERFOMANCES`),
      subTitle: `(${t(`${TP}.FN_NEXT_30_DAYS`)})`,
      value: 'upcoming',
      isShown: true,
    },
  ];

  const onChangeTab = tabValue => {
    let value;
    if (page === PAGE_NAMES.PRODUCTION) {
      value = PRODUCTION_VIEW_TYPES.PERFORMANCE;
    } else {
      value = tabValue === 'all' ? PRODUCTION_VIEW_TYPES.PRODUCTION : PRODUCTION_VIEW_TYPES.PERFORMANCE;
    }
    state.productions = {};
    dispatch({
      type: 'UPDATE_TAB_VIEW',
      payload: {
        tabViewType: tabValue,
      },
    });
    dispatch({
      type: 'UPDATE_VIEW',
      payload: {
        viewType: value,
      },
    });

    let newQuery;
    if (tabValue === 'all') {
      newQuery = {
        ...state.query,
        sort: page === PAGE_NAMES.PRODUCTION ? '-date' : '-year',
      };
    } else {
      newQuery = {
        ...state.query,
        date_from: createDate(new Date()).format(dateFormats.basic),
        date_to: createDate(new Date())
          .add('30', 'days')
          .format(dateFormats.basic),
        sort: 'date',
      };
    }
    fetchPerformances(newQuery, { limit: 10, page: 1 }, value);
  };

  return (
    <>
      {showSubFilters ? (
        <SubFilterSection
          options={subFilterOptions}
          isSmall={isSmall}
          onChange={onSubfilterChange}
          query={initialQuery}
          className={className}
          tabViewType={state.tabViewType}
          selectedValueQuery={{
            ...state.query,
            year_id: get(festivalInfo, 'year'),
            ...(getSeason() && getSeason()),
          }}
          seasonsOptions={seasonsOptions}
          hasSubFilterSelectedValues={hasSubFilterSelectedValues}
        />
      ) : null}
      <StubPagePerformanceContent
        tab={tab}
        linkProps={linkProps}
        title={getTitle(page, t)}
        isLoggedIn={isLoggedIn}
        query={state.query}
        performances={state.productions}
        fetchPerformances={fetchPerformances}
        page={page}
        dates={dates}
        profile={profile}
        pageView={state.viewType}
        performanceTabs={performanceTabs}
        hideViewPerformance={hideViewPerformance}
        viewAllPerformances={goToTab}
        isLoading={state.isLoadingProductions}
        onChangeTab={onChangeTab}
        selectedTab={state.tabViewType}
        onCalendarViewMonthChange={fetchPerformanceForMonth}
      />
    </>
  );
};
